export interface ParsedLyricWord {
  text: string;
  startMs: number;
  endMs: number;
}

export interface ParsedLyricLine {
  lineIndex: number;
  startMs: number;
  endMs: number;
  text: string;
  words: ParsedLyricWord[];
}

// stable-ts `result.to_lrc(segment_level=True, word_level=True)` emits one
// line tag per segment, with inline word tags for the words it contains.
// Metadata tags ([ti:...], [ar:...], ...) and blank lines don't match this
// (non-digit content before the colon / no brackets at all) and are skipped.
const LINE_TAG = /^\[(\d+):(\d{2})[.:](\d{2,3})\](.*)$/;
const WORD_TAG = /<(\d+):(\d{2})[.:](\d{2,3})>([^<]*)/g;

// LRC records when a word starts, never when it ends. A word ends where the
// next word in its line starts; a line's last word is held LAST_WORD_MS, or
// until the next line starts if that is sooner, and the line ends with it.
// The device's LRC parser guessed the same, so rows timed this way show as
// they did while the device read LRC.
const LAST_WORD_MS = 500;

/** Every group in LINE_TAG/WORD_TAG is mandatory, so a successful `.exec()`
 * always populates all of them -> failing this is an engine bug, not bad
 * input. Throws instead of a non-null assertion, so a bug here surfaces
 * loudly rather than laundering `undefined` into `NaN` timestamps. */
function group(match: RegExpExecArray, index: number): string {
  const value = match[index];
  if (value === undefined) {
    throw new Error(`parseWordLrc: regex group ${index} unexpectedly missing`);
  }
  return value;
}

function toMs(min: string, sec: string, frac: string): number {
  const fracMs = frac.length === 2 ? Number(frac) * 10 : Number(frac);
  return (Number(min) * 60 + Number(sec)) * 1000 + fracMs;
}

function toMsFromMatch(match: RegExpExecArray): number {
  return toMs(group(match, 1), group(match, 2), group(match, 3));
}

interface RawLine {
  startMs: number;
  rest: string;
}

function parseRawLines(lrcText: string): RawLine[] {
  const rawLines: RawLine[] = [];
  for (const line of lrcText.split(/\r?\n/)) {
    const match = LINE_TAG.exec(line);
    if (!match) continue;
    rawLines.push({ startMs: toMsFromMatch(match), rest: group(match, 4) });
  }
  return rawLines;
}

function parseWords(raw: RawLine): ParsedLyricWord[] {
  const words: ParsedLyricWord[] = [];
  WORD_TAG.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = WORD_TAG.exec(raw.rest))) {
    words.push({ text: group(match, 4).trim(), startMs: toMsFromMatch(match), endMs: 0 });
  }
  // No word tags on this line (plain, line-level-only LRC) -> one word
  // spanning the whole line.
  if (words.length === 0) {
    words.push({ text: raw.rest.trim(), startMs: raw.startMs, endMs: 0 });
  }
  return words;
}

interface Span {
  startMs: number;
  endMs: number;
}

/** End times for lines whose words carry only their starts: parsed LRC, and
 * rows timed before ends were inferred this way (the lyrics.json backfill).
 * See LAST_WORD_MS. A line without words keeps the times it has. */
export function inferEnds<L extends Span & { words: readonly Span[] }>(lines: readonly L[]): L[] {
  return lines.map((line, i) => {
    const last = line.words[line.words.length - 1];
    if (!last) return line;
    const nextLineStartMs = lines[i + 1]?.startMs ?? Infinity;
    const endMs = Math.max(last.startMs, Math.min(last.startMs + LAST_WORD_MS, nextLineStartMs));
    const words = line.words.map((word, w) => ({ ...word, endMs: line.words[w + 1]?.startMs ?? endMs }));
    return { ...line, endMs, words };
  });
}

/**
 * Parses enhanced LRC (the pipeline's alignment output, or an import): line
 * tags `[mm:ss.xx]` carrying inline word tags `<mm:ss.xx>word`. LRC has no
 * end times; inferEnds supplies them.
 */
export function parseWordLrc(lrcText: string): ParsedLyricLine[] {
  const lines = parseRawLines(lrcText).map((raw, i) => {
    const words = parseWords(raw);
    return {
      lineIndex: i,
      startMs: raw.startMs,
      endMs: raw.startMs,
      text: words
        .map((w) => w.text)
        .join(" ")
        .trim(),
      words,
    };
  });
  return inferEnds(lines);
}

function lrcTime(ms: number): string {
  const total = Math.max(0, Math.round(ms / 10));
  const minutes = Math.floor(total / 6000);
  const seconds = ((total % 6000) / 100).toFixed(2).padStart(5, "0");
  return `${String(minutes).padStart(2, "0")}:${seconds}`;
}

/** Enhanced LRC from lyric rows, in the shape parseWordLrc and the device's
 * LrcParser read: one `[mm:ss.xx]` line per row, each word tagged
 * `<mm:ss.xx>` with its start. A line with no words is its text alone. The
 * lyrics editor's export. */
export function formatWordLrc(
  lines: ReadonlyArray<{ startMs: number; text: string; words: ReadonlyArray<{ text: string; startMs: number }> }>,
): string {
  return lines
    .map((line) => {
      const words = line.words.filter((word) => word.text.trim());
      const body = words.length
        ? words.map((word) => `<${lrcTime(word.startMs)}>${word.text.trim()}`).join(" ")
        : line.text.trim();
      return `[${lrcTime(line.startMs)}]${body}`;
    })
    .join("\n")
    .concat("\n");
}
